import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/primitives/Button';
import { reloadToLatest, useServiceWorkerState } from '@/pwa/swState';

interface VersionInfo {
    version: string;
    commit: string;
    describe: string;
    built: string;
}

/** The commit this bundle was built from (vite `define`), or null when not defined (tests, SSR) or unknown. */
function runningCommit(): string | null {
    try {
        const v = __BUILD_COMMIT__;
        return typeof v === 'string' && v && v !== 'unknown' ? v : null;
    } catch {
        return null;
    }
}

/**
 * True when the server's build is not the code running in this tab. The bundle carries the short sha
 * (`git rev-parse --short`), version.json the full one, so the full sha must start with the short one.
 * An unknown build commit never claims staleness.
 */
export function isStaleBuild(running: string | null, served: string): boolean {
    if (!running) return false;
    return !served.toLowerCase().startsWith(running.toLowerCase());
}

/**
 * Always-on build stamp, bottom-right.
 *
 * Exists because "which build am I actually looking at?" was repeatedly the
 * wrong answer during the iPad work -- a stale preview server and a stale deploy
 * both produced confident debugging against code that was not running. A visible
 * commit makes that class of mistake self-evident instead of invisible, for a
 * human on a device as well as for anyone reading a screenshot.
 *
 * Reads /version.json (what the SERVER has now) and compares it with the commit
 * baked into this bundle (what the TAB runs). They differ when the tab still runs
 * an older deploy (a long-open tab, or a page cached before the no-cache headers,
 * docs/hosting-headers.md): the badge then says "Update available" with a Reload
 * button. It re-checks whenever the tab becomes visible again, so a tab left open
 * across a deploy notices it. It is independent of the analytics pipeline, which
 * is gated to the production hostname and would leave the badge blank elsewhere.
 */
export function VersionBadge() {
    const [info, setInfo] = useState<VersionInfo | null>(null);
    const { needRefresh } = useServiceWorkerState();

    useEffect(() => {
        if (typeof fetch !== 'function') return;
        let cancelled = false;
        const check = () => {
            fetch('/version.json', { cache: 'no-store' })
                .then((r) => (r.ok ? r.json() : null))
                .then((data: unknown) => {
                    if (cancelled || !data || typeof data !== 'object') return;
                    const d = data as Partial<VersionInfo>;
                    if (typeof d.commit !== 'string') return;
                    setInfo({
                        version: typeof d.version === 'string' ? d.version : '?',
                        commit: d.commit,
                        describe: typeof d.describe === 'string' ? d.describe : d.commit.slice(0, 7),
                        built: typeof d.built === 'string' ? d.built : '',
                    });
                })
                .catch(() => {
                    // A missing version.json is not worth surfacing; the badge just
                    // stays hidden (or keeps its last answer), which is itself a signal.
                });
        };
        const onVisibility = () => {
            if (document.visibilityState === 'visible') check();
        };
        check();
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            cancelled = true;
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);

    if (!info) return null;

    const short = info.commit.slice(0, 7);
    const builtLocal = info.built ? new Date(info.built).toLocaleString() : 'unknown';
    const running = runningCommit();

    // A waiting service worker has its own notice (UpdateNotice): one Reload on screen, not two. (Dismissing that
    // notice brings this stale state back only when /version.json also differs.)
    if (isStaleBuild(running, info.commit) && !needRefresh) {
        return (
            <div
                className="version-badge version-badge--stale"
                data-testid="version-badge"
                data-commit={info.commit}
                data-running-commit={running ?? ''}
                data-stale="true"
                role="status"
                title={`This tab runs ${running}. The server has v${info.version} ${short}, built ${builtLocal}.`}
            >
                <span>Update available</span>
                <Button size="small" variant="primary" onClick={() => { void reloadToLatest(); }} data-testid="version-badge-reload">
                    Reload
                </Button>
            </div>
        );
    }

    return (
        <div
            className="version-badge"
            data-testid="version-badge"
            data-commit={info.commit}
            title={`v${info.version} - ${info.describe} - built ${builtLocal}`}
        >
            v{info.version} {short}
        </div>
    );
}
